// Phase 0 DETERMINISM GATE — state hashing.
//
// hashState(state) folds the determinism-relevant slice of a GameState into a stable FNV-1a hex
// string. It is PURE (no mutation, no DOM, no clocks) and its output depends ONLY on the real
// field values — flip any tracked bit and the hash changes; it is never a constant.
//
// Determinism-relevant fields (everything that must agree byte-for-byte across two engines running
// the same seeded lockstep sim):
//   - each unit, sorted by id: pos.x, pos.y, hp, kind, side, moveTarget(.x/.y or "null")
//   - each colony (you, bot): res.energy/amino/toxin + genome levels (keys sorted)
//   - state.clocks.sim
//   - state.result (over / winner / reason)
//
// Floats are quantized deterministically via Math.round(x * 1000) BEFORE folding so that
// sub-milliunit FP noise never perturbs the hash, while any real divergence does. We fold the
// decimal string of the quantized integer (not raw bytes) so there is zero 32-bit-overflow
// ambiguity across engines.

import type { GameState, Unit, Colony, Side, Vec } from '../types.js';

const FNV_OFFSET = 0x811c9dc5; // 2166136261
const FNV_PRIME = 0x01000193; // 16777619

/** Fold every char code of a string into a running FNV-1a 32-bit hash. */
function foldStr(h: number, s: string): number {
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // (h * FNV_PRIME) >>> 0, done via Math.imul to stay in 32-bit integer space on every engine.
    h = Math.imul(h, FNV_PRIME) >>> 0;
  }
  return h;
}

/** Quantize a float deterministically, then fold its decimal string (handles -0 and NaN safely). */
function foldNum(h: number, x: number): number {
  const q = Number.isFinite(x) ? Math.round(x * 1000) : 0;
  // Normalize -0 to 0 so "0" and "-0" never diverge across engines.
  return foldStr(h, String(q === 0 ? 0 : q));
}

/** Fold a nullable move target: either its quantized x/y or the literal token "null". */
function foldMoveTarget(h: number, mt: Vec | null): number {
  if (mt == null) return foldStr(h, 'null');
  h = foldNum(h, mt.x);
  h = foldNum(h, mt.y);
  return h;
}

function foldUnit(h: number, u: Unit): number {
  h = foldStr(h, u.id);
  h = foldNum(h, u.pos.x);
  h = foldNum(h, u.pos.y);
  h = foldNum(h, u.hp);
  h = foldStr(h, u.kind);
  h = foldStr(h, u.side);
  h = foldMoveTarget(h, u.moveTarget);
  return h;
}

function foldColony(h: number, c: Colony): number {
  h = foldStr(h, c.side);
  h = foldNum(h, c.res.energy);
  h = foldNum(h, c.res.amino);
  h = foldNum(h, c.res.toxin);
  // genome: Record<mutationId, level>. Sort keys so insertion order never leaks into the hash.
  const keys = Object.keys(c.genome).sort();
  for (const k of keys) {
    h = foldStr(h, k);
    h = foldNum(h, c.genome[k]);
  }
  return h;
}

/**
 * Pure FNV-1a hex hash over the determinism-relevant slice of a GameState.
 * Depends only on real field values — never a constant.
 */
export function hashState(state: GameState): string {
  let h = FNV_OFFSET >>> 0;

  // Units, sorted by id (stable, engine-independent order). Copy before sort — never mutate state.
  const units = state.units
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  h = foldStr(h, 'U');
  h = foldNum(h, units.length);
  for (const u of units) h = foldUnit(h, u);

  // Colonies, in a fixed side order.
  h = foldStr(h, 'C');
  const sides: Side[] = ['you', 'bot'];
  for (const side of sides) {
    const col = state.colonies[side];
    if (col) h = foldColony(h, col);
    else h = foldStr(h, 'nocol');
  }

  // Clocks.sim + match result.
  h = foldStr(h, 'K');
  h = foldNum(h, state.clocks.sim);
  h = foldStr(h, 'R');
  h = foldStr(h, state.result.over ? '1' : '0');
  h = foldStr(h, state.result.winner ?? 'none');
  h = foldStr(h, state.result.reason ?? '');

  // 8-char zero-padded hex.
  return (h >>> 0).toString(16).padStart(8, '0');
}
